import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../provider/auth';
import { get_user_info } from '../../api/functions';
import { LoadingSpinner } from '../../components';
import { ErrorScreen } from '../';
import './AdminPanel.css';
import { useApi } from '../../api/api';
import EventsTab from './EventsTab';
import NewsTab from './NewsTab';
import InfoPagesTab from './InfoPagesTab';
import DocumentsTab from './DocumentsTab';
import ImagesTab from './ImagesTab';
import SectionsTab from './SectionsTab';

const AdminPanel = () => {
  const { t } = useTranslation();
  const { tab } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(tab || 'events');
  const { token, isLoading: authLoading, isAuthenticated } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const getUserInfoApi = useApi(get_user_info);

  useEffect(() => {
    if (!tab) {
      navigate('/admin/news', { replace: true });
      return;
    }
    setActiveTab(tab);
  }, [tab, navigate]);

  useEffect(() => {
    if (authLoading) return;

    const checkAdminStatus = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let userInfo = null;
        if (isAuthenticated && token) {
          try {
            userInfo = await getUserInfoApi.execute();
          } catch (err) {
            console.error('Error fetching user info:', err);
          }
        }
        if (userInfo && userInfo.is_admin) {
          setIsAdmin(true);
          setError(null);
        } else {
          setIsAdmin(false);
          setError(403);
        }
      } catch (err) {
        console.error('Error fetching user info:', err);
        setError('Failed to verify permissions');
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminStatus();
  }, [isAuthenticated]);

  const tabs = [
    { id: 'news', label: 'News', icon: '📰' },
    { id: 'events', label: 'Events', icon: '📅' },
    { id: 'infopages', label: 'Info Pages', icon: 'ℹ️' },
    { id: 'documents', label: 'Documents', icon: '🗃️' },
    { id: 'images', label: 'Images', icon: '🖼️' },
    { id: 'sections', label: 'Sections', icon: '📑' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'events':
        return <EventsTab />;
      case 'news':
        return <NewsTab />;
      case 'infopages':
        return <InfoPagesTab />;
      case 'documents':
        return <DocumentsTab />;
      case 'images':
        return <ImagesTab />;
      case 'sections':
        return <SectionsTab />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="admin-panel">
        <main className="admin-panel-main">
          <div className="admin-panel-container">
            <LoadingSpinner message="Verifying permissions..." />
          </div>
        </main>
      </div>
    );
  }

  /* if ((!isAuthenticated && !isLoading && !getUserInfoApi.loading && !authLoading) || error) {
    return <ErrorScreen error={401} />;
  }

  if (!isAdmin && !isLoading && !getUserInfoApi.loading && !authLoading) {
    return <ErrorScreen error={403} />;
  } */
  if (error) {
     return <ErrorScreen error={error} />;
   }

  return (
    <div className="admin-panel">
      <main className="admin-panel-main">
        <div className="admin-panel-container">
          <div className="admin-panel-header">
            <h1 className="admin-panel-title">{t('admin.title')}</h1>
          </div>

          <div className="admin-tabs">
            {tabs.map(tabItem => (
              <button
                key={tabItem.id}
                className={`admin-tab ${activeTab === tabItem.id ? 'active' : ''}`}
                onClick={() => navigate(`/admin/${tabItem.id}`)}
              >
                <span className="admin-tab-icon">{tabItem.icon}</span>
                <span className="admin-tab-label">{t(`admin.tabs.${tabItem.id}`)}</span>
              </button>
            ))}
          </div>

          <div className="admin-tab-content">
            {renderTabContent()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;
